import React, { useState, useMemo, useEffect } from "react";
import { DataTable, TableHeader, TableBody, TableRow, TableColumn, FontIcon, Button, Tooltipped } from "react-md";
import { MGridColumnDefinition } from "../MGrid.tsx";
import {
  QaTimeCardResponse,
  SchedulingTimeCardResponse,
  QaTimeCardResponseDetail,
  SchedulingTimeCardDetail,
} from "../../../interfaces/Mueller.Business.Models.ts";
import {
  DATA_TYPE_NUMBER,
  DATA_TYPE_NUMBER_HUNDREDTHS,
  DATA_TYPE_MONEY,
  DATA_TYPE_PERCENT_DECIMAL,
} from "typeConstants/dataTypes";
import { normalizeNumberVariableDecimal, formatNumberConsistentDecimalLength } from "components/FormElements";
import { moneyFormatter, formatDecimalPercent2 } from "components/Shared/Formatters";
import xlsx from "xlsx";
import { formatWorksheetColumns } from "../Functions.js";
import { transformEmployeeList } from "./Definitions.js";
import MSpinner from "../MSpinner.tsx";

// TODO: this could probably be a more generic grouped table component but there's simply no time atm

const expandCollapseColWidth = 40;

enum SortDirection {
  ASC = "ASC",
  DES = "DES",
  NONE = "NONE",
}

enum ExpandCollapseAllStates {
  EXPAND = "EXPAND",
  COLLAPSE = "COLLAPSE",
}

interface GroupedTimeLogTableProps {
  columnDefinitions: MGridColumnDefinition[]; // Reusing this for now as this is replacing an MGrid component
  data: QaTimeCardResponse[] | SchedulingTimeCardResponse[];
  onRowClick: (internetUserId: number) => void;
  downloadFileName: string;
  fetchingData: boolean;
}

interface TimeLogTableGroupProps {
  columnDefinitions: MGridColumnDefinition[];
  data: QaTimeCardResponse | SchedulingTimeCardResponse;
  sort?: SortedColumn;
  expandCollapseState?: ExpandCollapseAllStates;
  onRowClick: (internetUserId: number) => void;
}

interface SortedColumn {
  key: string;
  direction: SortDirection;
  dataType: string;
}

const getValueByPath = (obj: object, path: string) =>
  path.split(".").reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);

const getRowValue = (data: object, property: string, dataType: string) => {
  let value = getValueByPath(data, property);
  if (dataType) {
    const formatter = getFormatterForDataType(dataType);
    return formatter(value);
  }
  return value;
};

const compareWithObjects = (a: any, b: any, keys: string) => {
  const aValue = getValueByPath(a, keys);
  const bValue = getValueByPath(b, keys);

  if (aValue === undefined || bValue === undefined) {
    return aValue === bValue ? 0 : aValue === undefined ? -1 : 1;
  }

  if (typeof aValue === "number" && typeof bValue === "number") {
    return aValue - bValue;
  }

  return String(aValue).localeCompare(String(bValue));
};

// TODO: This should go some where that could be referenced by both MGrid and this component
const getFormatterForDataType = (dataType: string) => {
  // Only supporting types here that are used in TimeLogs/Definitions.js
  switch (dataType) {
    case DATA_TYPE_NUMBER:
      return normalizeNumberVariableDecimal();
    case DATA_TYPE_NUMBER_HUNDREDTHS:
      return formatNumberConsistentDecimalLength();
    case DATA_TYPE_MONEY:
      return moneyFormatter(2);
    case DATA_TYPE_PERCENT_DECIMAL:
      return formatDecimalPercent2(0, true);
  }
};

const TimeLogTableGroup = (props: TimeLogTableGroupProps) => {
  const [expanded, setExpanded] = useState<boolean>(false);
  const visibleColumnDefs = props.columnDefinitions?.filter(col => col.visible);

  useEffect(() => {
    if (props.expandCollapseState) {
      switch (props.expandCollapseState) {
        case ExpandCollapseAllStates.EXPAND:
          if (!expanded) setExpanded(true);
          break;
        case ExpandCollapseAllStates.COLLAPSE:
          if (expanded) setExpanded(false);
          break;
      }
    }
  }, [props.expandCollapseState]);

  const toggleExpanded = () => setExpanded(!expanded);

  const headerRow = useMemo(() => {
    // Add 1 to the width due to the expand/collapse column
    const colSpanWidth =
      (visibleColumnDefs?.slice(0, 3).reduce((acc, val) => acc + val.width, 0) ?? 0) + expandCollapseColWidth;
    return (
      <TableRow>
        {/* Add an extra column for the expand/collapse control */}
        <TableColumn style={{ minWidth: `${expandCollapseColWidth}px` }}>
          <FontIcon onClick={toggleExpanded}>{expanded ? "remove" : "add"}</FontIcon>
        </TableColumn>
        {/* Header group spans the first 4 columns */}
        <TableColumn colSpan={4} style={{ minWidth: `${colSpanWidth}px` }}>
          {props.data?.totals?.employeeName?.firstLast}
        </TableColumn>
        {visibleColumnDefs?.slice(4).map(({ key, tooltip, dataSource, width, dataType }, i) => (
          <TableColumn
            // @ts-ignore
            title={typeof tooltip === "string" ? tooltip : null}
            key={`${key}-${i}`}
            style={{ minWidth: `${width}px`, maxWidth: `${width}px`, textAlign: `${dataType ? "right" : "left"}` }}
          >
            {typeof dataSource === "function"
              ? dataSource(props.data?.totals)
              : getRowValue(props.data?.totals, dataSource, dataType)}
          </TableColumn>
        ))}
      </TableRow>
    );
  }, [props.columnDefinitions, props.data, expanded]);

  const tableRows = useMemo(() => {
    if (!expanded) return <></>;

    const rowData = transformEmployeeList([...props.data?.employeeList]);

    if (props.sort && props.sort?.direction !== SortDirection.NONE) {
      const comparer = (
        a: QaTimeCardResponseDetail | SchedulingTimeCardDetail,
        b: QaTimeCardResponseDetail | SchedulingTimeCardDetail
      ) => {
        // If there's no datatype its a string
        switch (props.sort.direction) {
          case SortDirection.ASC:
            return compareWithObjects(a, b, props.sort.key);
          case SortDirection.DES:
            return compareWithObjects(b, a, props.sort.key);
          case SortDirection.NONE:
          default:
            return 0;
        }
      };
      rowData.sort(comparer);
    }

    const rows = rowData.map((row: SchedulingTimeCardDetail | QaTimeCardResponseDetail, rowIndex: number) => (
      <TableRow onClick={() => props.onRowClick(row.internetUserId)} key={`${row.internetUserId}-${rowIndex}`}>
        {/* Empty cell for expand collapse column */}
        <TableColumn style={{ minWidth: `${expandCollapseColWidth}px` }} className="m-table-data-column"></TableColumn>
        {visibleColumnDefs?.map(({ key, tooltip, dataSource, width, dataType }, colIndex) => (
          <TableColumn
            // @ts-ignore
            title={typeof tooltip === "function" ? tooltip(row) : tooltip}
            className="m-table-data-column"
            key={`${key}-${rowIndex}-${colIndex}`}
            style={{ minWidth: `${width}px`, maxWidth: `${width}px`, textAlign: `${dataType ? "right" : "left"}` }}
          >
            {typeof dataSource === "function" ? dataSource(row) : getRowValue(row, dataSource, dataType)}
          </TableColumn>
        ))}
      </TableRow>
    ));

    return rows;
  }, [props.columnDefinitions, props.data, props.sort, expanded]);

  return (
    <DataTable plain style={{ fontSize: "12px" }}>
      <TableHeader>{headerRow}</TableHeader>
      <TableBody>{tableRows}</TableBody>
    </DataTable>
  );
};

const GroupedTimeLogTable = (props: GroupedTimeLogTableProps) => {
  const [sort, setSort] = useState<SortedColumn>(undefined);
  const [expandCollapseAll, setExpandCollapseAll] = useState<ExpandCollapseAllStates>(ExpandCollapseAllStates.COLLAPSE);
  const visibleColumnDefs = props.columnDefinitions?.filter(col => col.visible);

  const handleSort = (key: string, dataType: string) => {
    if (sort && sort.key === key) {
      switch (sort.direction) {
        case SortDirection.ASC:
          setSort((prevState) => ({ ...prevState, direction: SortDirection.DES }));
          break;
        case SortDirection.DES:
          setSort((prevState) => ({ ...prevState, direction: SortDirection.NONE }));
          break;
        case SortDirection.NONE:
        default:
          setSort((prevState) => ({ ...prevState, direction: SortDirection.ASC }));
          break;
      }
    } else {
      setSort({ key: key, direction: SortDirection.ASC, dataType: dataType });
    }
  };

  const getHeaderIconForSort = (sortDirection: SortDirection) => {
    switch (sortDirection) {
      case SortDirection.ASC:
        return "arrow_drop_up";
      case SortDirection.DES:
        return "arrow_drop_down";
      case SortDirection.NONE:
        return "";
    }
  };

  const onDownload = () => {
    const displayColumns = props.columnDefinitions.filter((c) => !c.hideFromExport);
    const cols = displayColumns.map((c) => c.name);
    const allEmployees = props.data.flatMap((d) => d.employeeList || []);
    const rows = allEmployees.map((row) =>
      displayColumns.reduce((acc, cd) => {
        acc[cd.name] = typeof cd.dataSource === "function" ? cd.dataSource(row) : getRowValue(row, cd.key, cd.dataType);
        return acc;
      }, {})
    );

    const wb = xlsx.utils.book_new();
    let ws = xlsx.utils.json_to_sheet(rows, { header: cols });
    formatWorksheetColumns(displayColumns, ws);
    xlsx.utils.book_append_sheet(wb, ws, "Sheet 1");
    xlsx.writeFile(wb, `${props.downloadFileName}.xlsx`);
  };

  const toggleExpandCollapseAll = () => {
    switch (expandCollapseAll) {
      case ExpandCollapseAllStates.EXPAND:
        setExpandCollapseAll(ExpandCollapseAllStates.COLLAPSE);
        break;
      case ExpandCollapseAllStates.COLLAPSE:
        setExpandCollapseAll(ExpandCollapseAllStates.EXPAND);
        break;
    }
  };

  let tableData = useMemo(() => {
    // The first entry is the totals and has no employeeList
    let data = [...props.data.slice(1)];
    if (sort && sort.direction !== SortDirection.NONE) {
      const comparer = (
        a: QaTimeCardResponse | SchedulingTimeCardResponse,
        b: QaTimeCardResponse | SchedulingTimeCardResponse
      ) => {
        // If there's no datatype its a string
        switch (sort?.direction) {
          case SortDirection.ASC:
            return compareWithObjects(a, b, `totals.${sort.key}`);
          case SortDirection.DES:
            return compareWithObjects(b, a, `totals.${sort.key}`);
          case SortDirection.NONE:
          default:
            return 0;
        }
      };
      data.sort(comparer);
    }
    return data;
  }, [sort, props.data]);

  let totalsRow = useMemo(() => {
    if (props.data?.length) {
      return (
        <TableRow key={`expandCollapse-${props.data[0]?.totals.employeeName.firstLast}`} className="totals-row">
          {/* Add an extra column for the expand/collapse control */}
          <TableColumn
            key={`expandCollapse-${props.data[0]?.totals.employeeName}`}
            style={{ minWidth: `${expandCollapseColWidth}px` }}
            className="m-table-data-column"
          />
          {visibleColumnDefs?.map(({ key, tooltip, dataSource, width, dataType }, i) => (
            <TableColumn
              // @ts-ignore
              title={typeof tooltip === "string" ? tooltip : null}
              className="m-table-data-column"
              key={`${key}-${i}`}
              style={{ minWidth: `${width}px`, maxWidth: `${width}px`, textAlign: `${dataType ? "right" : "left"}` }}
            >
              {typeof dataSource === "function"
                ? dataSource(props.data[0]?.totals)
                : getRowValue(props.data[0]?.totals, dataSource, dataType)}
            </TableColumn>
          ))}
        </TableRow>
      );
    }
  }, [props.data, props.columnDefinitions]);

  if (props.fetchingData) {
    return <MSpinner />;
  }

  return (
    <>
      <div style={{ display: "flex", flexDirection: "row-reverse", padding: "8px" }}>
        <Button raised primary onClick={onDownload} disabled={!props.data}>
          Download
        </Button>
      </div>
      <DataTable plain className="m-table grouped-time-log-data-table">
        <TableHeader>
          <TableRow className="header-row">
            {/* Cell for expand collapse column */}
            <TableColumn
              title="Expand/Collapse"
              key="expandCollapse"
              onClick={toggleExpandCollapseAll}
              style={{ minWidth: `${expandCollapseColWidth}px`, cursor: "pointer" }}
            >
              <FontIcon>expand_circle_down</FontIcon>
            </TableColumn>
            {visibleColumnDefs?.map(({ key, name, width, dataType, headerTooltip }, i) => (
              <TableColumn
                // @ts-ignore
                title={headerTooltip}
                key={`${key}-${i}`}
                onClick={() => handleSort(key, dataType)}
                style={{ minWidth: `${width}px`, maxWidth: `${width}px`, cursor: "pointer" }}
              >
                {name}
                {sort && sort.key === key && sort.direction !== SortDirection.NONE && (
                  <FontIcon>{getHeaderIconForSort(sort.direction)}</FontIcon>
                )}
              </TableColumn>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {totalsRow}
          {/* Add 1 for expand collapse column */}
          <TableColumn colSpan={(visibleColumnDefs.length ?? 0) + 1} style={{ padding: "0px" }}>
            {tableData.map((d: SchedulingTimeCardResponse | QaTimeCardResponse, i: number) => (
              <TimeLogTableGroup
                key={`tableGroup-${i}`}
                columnDefinitions={visibleColumnDefs}
                data={d}
                sort={sort}
                expandCollapseState={expandCollapseAll}
                onRowClick={props.onRowClick}
              />
            ))}
          </TableColumn>
        </TableBody>
      </DataTable>
    </>
  );
};

export default GroupedTimeLogTable;
